import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Button,
  Input,
  Typography
} from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDownload,
  faEnvelope,
  faPrint,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { useAuth } from '../AuthContext';
import { rootUrl } from '../../../config/config';
import { openWhatsapp } from '../../../utils/whatsapp';
import { DEFAULT_COMPANY_INFO, fetchCompanyInfo } from '../../../utils/companyInfo';
import DocumentPreviewScaler from './DocumentPreviewScaler';
import OwnerTransactionPrintSheet from './OwnerTransactionPrintSheet';
import { buildOwnerTransactionFilename, generateOwnerTransactionPdf } from '../../../utils/ownerTransactionPDFGenerator';

const getDocumentLabel = (transaction) => {
  const type = String(transaction?.trans_type || '').toUpperCase();
  const labels = {
    REM: 'Remittance',
    PAY: 'Payment',
    RIM: 'Reimbursement',
    CHG: 'Charge',
    ADJ: 'Adjustment',
    MOI: 'Monthly Invoice',
    JIN: 'Job Card Invoice',
    REV: 'Reversal'
  };

  return labels[type] || transaction?.short_desc || type || 'Owner Transaction';
};

const isReversedDocument = (transaction) => (
  transaction?.reversed === true
  || transaction?.reversed === 1
  || transaction?.reversed === '1'
  || String(transaction?.reference_type || '').toUpperCase() === 'REVERSAL'
);

const OwnerTransactionPreviewDialog = ({ open, onClose, transaction }) => {
  const { token, user } = useAuth();
  const authToken = token || localStorage.getItem('authToken');
  const [companyInfo, setCompanyInfo] = useState(DEFAULT_COMPANY_INFO);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [busyAction, setBusyAction] = useState('');
  const [status, setStatus] = useState({ type: '', text: '' });

  const filename = useMemo(() => buildOwnerTransactionFilename(transaction), [transaction]);
  const reversedDocument = isReversedDocument(transaction);

  useEffect(() => {
    if (!open) {
      return;
    }

    setStatus({ type: '', text: '' });
    setRecipientEmail(transaction?.owner_email || '');
    setEmailMessage(
      transaction?.owner_name
        ? `Hello ${transaction.owner_name},\n\nPlease find attached the ${getDocumentLabel(transaction).toLowerCase()} document.\n\nRegards,\n${user?.username || 'Fleet Management'}`
        : `Please find attached the ${getDocumentLabel(transaction).toLowerCase()} document.`
    );

    fetchCompanyInfo(authToken).then(setCompanyInfo);
  }, [open, transaction, authToken, user?.username]);

  const setBusy = (action, value = true) => {
    setBusyAction(value ? action : '');
  };

  const generatePdf = async () => {
    const pdf = await generateOwnerTransactionPdf();
    return pdf;
  };

  const downloadPdf = async () => {
    try {
      setBusy('download');
      setStatus({ type: '', text: '' });
      const pdf = await generatePdf();
      pdf.save(filename);
      setStatus({ type: 'success', text: 'PDF downloaded successfully.' });
    } catch (error) {
      setStatus({ type: 'error', text: error.message || 'Failed to download PDF.' });
    } finally {
      setBusy('download', false);
    }
  };

  const printPdf = async () => {
    let printFrame = null;
    const cleanup = () => {
      if (printFrame && printFrame.parentNode) {
        printFrame.parentNode.removeChild(printFrame);
      }
      printFrame = null;
    };

    try {
      setBusy('print');
      setStatus({ type: '', text: '' });
      const pdf = await generatePdf();
      const blobUrl = pdf.output('bloburl');
      printFrame = document.createElement('iframe');
      printFrame.style.position = 'fixed';
      printFrame.style.right = '0';
      printFrame.style.bottom = '0';
      printFrame.style.width = '0';
      printFrame.style.height = '0';
      printFrame.style.border = '0';
      printFrame.style.visibility = 'hidden';
      printFrame.src = blobUrl;

      document.body.appendChild(printFrame);

      printFrame.onload = () => {
        try {
          const frameWindow = printFrame.contentWindow;
          if (frameWindow) {
            frameWindow.onafterprint = cleanup;
            frameWindow.focus();
            frameWindow.print();
          }
        } catch (error) {
          window.open(blobUrl, '_blank', 'noopener,noreferrer');
          cleanup();
        }
      };

      window.setTimeout(cleanup, 30000);
    } catch (error) {
      cleanup();
      setStatus({ type: 'error', text: error.message || 'Failed to print PDF.' });
    } finally {
      setBusy('print', false);
    }
  };

  const shareOnWhatsApp = async () => {
    try {
      setBusy('whatsapp');
      setStatus({ type: '', text: '' });
      const response = await axios.post(
        `${rootUrl}/api/owners/${encodeURIComponent(transaction.owner_code)}/transactions/${encodeURIComponent(transaction.id)}/share-link`,
        {},
        {
          headers: {
            Authorization: `Bearer ${authToken}`
          }
        }
      );

      const shareToken = response.data?.data?.share_token;
      if (!shareToken) {
        throw new Error('Failed to create share link');
      }

      const shareUrl = `${rootUrl}/share/owner-document/${encodeURIComponent(shareToken)}?download=1`;
      const shareText = [
        `${getDocumentLabel(transaction)} document`,
        transaction?.owner_name ? `Owner: ${transaction.owner_name}` : '',
        transaction?.id ? `Transaction: ${transaction.id}` : '',
        transaction?.date ? `Date: ${transaction.date}` : '',
        `Download: ${shareUrl}`
      ].filter(Boolean).join('\n');

      openWhatsapp(transaction?.owner_phone, shareText);

      setStatus({ type: 'success', text: 'Share action opened successfully.' });
    } catch (error) {
      setStatus({ type: 'error', text: error.message || 'Failed to share document.' });
    } finally {
      setBusy('whatsapp', false);
    }
  };

  const emailPdf = async () => {
    if (!recipientEmail) {
      setStatus({ type: 'error', text: 'Recipient email is required.' });
      return;
    }

    try {
      setBusy('email');
      setStatus({ type: '', text: '' });
      const pdf = await generatePdf();
      const blob = pdf.output('blob');
      const formData = new FormData();
      formData.append('recipient_email', recipientEmail);
      formData.append('subject', `${transaction.owner_name || transaction.owner_code} - ${getDocumentLabel(transaction)} Document`);
      formData.append('message', emailMessage);
      formData.append('filename', filename);
      formData.append('pdf_file', blob, filename);

      const response = await axios.post(
        `${rootUrl}/api/owners/${encodeURIComponent(transaction.owner_code)}/transactions/${encodeURIComponent(transaction.id)}/send-email`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${authToken}`
          }
        }
      );

      if (response.data?.success) {
        setStatus({ type: 'success', text: 'Email sent successfully.' });
      } else {
        setStatus({ type: 'error', text: response.data?.message || 'Failed to send email.' });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        text: error.response?.data?.message || error.message || 'Failed to send email.'
      });
    } finally {
      setBusy('email', false);
    }
  };

  const statusClass = status.type === 'success'
    ? 'bg-green-50 text-green-700 border-green-200'
    : status.type === 'error'
      ? 'bg-red-50 text-red-700 border-red-200'
      : 'bg-slate-50 text-slate-700 border-slate-200';

  return (
    <Dialog open={open} handler={onClose} size="xl">
      <DialogHeader className="flex items-center justify-between gap-4">
        <div>
          <Typography variant="h5" className="text-[#2a2e30]">
            {getDocumentLabel(transaction)} Preview
          </Typography>
          <Typography variant="small" className="text-gray-500">
            {transaction?.owner_name || transaction?.owner_code || '-'} | {transaction?.id || '-'}
          </Typography>
          {reversedDocument && (
            <div className="mt-2 inline-flex items-center rounded-full bg-red-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-red-700">
              Reversed document
            </div>
          )}
        </div>
        <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </DialogHeader>

      <DialogBody className="space-y-4 overflow-y-auto max-h-[82vh]">
        {status.text && (
          <div className={`rounded-lg border px-4 py-3 text-sm ${statusClass}`}>
            {status.text}
          </div>
        )}
        {reversedDocument && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
            This document has been reversed and should not be treated as a valid active document.
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
          <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm overflow-hidden">
            <div className="mx-auto w-full">
              <DocumentPreviewScaler>
              <OwnerTransactionPrintSheet transaction={transaction} companyInfo={companyInfo} />
              </DocumentPreviewScaler>
            </div>
          </div>

          <div className="space-y-4 rounded-xl border border-gray-200 bg-slate-50 p-4">
            <div>
              <Typography variant="small" className="mb-2 font-semibold text-[#2a2e30]">
                Recipient Email
              </Typography>
              <Input
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                label="Email"
              />
            </div>

            <div>
              <Typography variant="small" className="mb-2 font-semibold text-[#2a2e30]">
                Message
              </Typography>
              <textarea
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                rows={8}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 outline-none focus:border-[#FF5733]"
              />
            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-1">
              <Button
                onClick={emailPdf}
                disabled={busyAction === 'email'}
                className="w-full bg-[#FF5733] text-white shadow-none"
              >
                <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                {busyAction === 'email' ? 'Sending...' : 'Send Email'}
              </Button>
              <Button
                onClick={printPdf}
                disabled={busyAction === 'print'}
                className="w-full border border-[#FF5733] bg-white text-[#FF5733] shadow-sm hover:bg-[#FFF3EE] hover:shadow-md"
                style={{ backgroundColor: '#ffffff', color: '#FF5733' }}
              >
                <FontAwesomeIcon icon={faPrint} className="mr-2" />
                {busyAction === 'print' ? 'Printing...' : 'Print'}
              </Button>
              <Button
                onClick={shareOnWhatsApp}
                disabled={busyAction === 'whatsapp'}
                className="w-full bg-green-600 text-white shadow-none"
              >
                <FontAwesomeIcon icon={faWhatsapp} className="mr-2" />
                {busyAction === 'whatsapp' ? 'Sharing...' : 'Share on WhatsApp'}
              </Button>
              <Button
                onClick={downloadPdf}
                disabled={busyAction === 'download'}
                className="w-full border border-[#0f766e] bg-white text-[#0f766e] shadow-sm hover:bg-[#ecfdf5] hover:shadow-md"
                style={{ backgroundColor: '#ffffff', color: '#0f766e' }}
              >
                <FontAwesomeIcon icon={faDownload} className="mr-2" />
                {busyAction === 'download' ? 'Preparing...' : 'Download PDF'}
              </Button>
            </div>

            <div className="text-xs text-gray-500 leading-5">
              The preview uses the printable document layout. Email sending requires the recipient address to be valid and the mail provider to be configured on the server.
            </div>
          </div>
        </div>
      </DialogBody>

      <DialogFooter>
        <Button variant="text" onClick={onClose} className="text-slate-700 shadow-none">
          Close
        </Button>
      </DialogFooter>
    </Dialog>
  );
};

export default OwnerTransactionPreviewDialog;
